/**
 * TanStack Query Client factory.
 *
 * `'use client'` modules still execute during the SERVER render pass in
 * Next.js, so a module-scope `new QueryClient()` singleton is shared across
 * every concurrent SSR request — a cross-request cache-bleed footgun (#3129).
 * `getQueryClient()` is the canonical fix: a fresh client per SSR
 * request, one long-lived client per browser tab.
 *
 * Use Cases:
 * - `app/providers.tsx` — the app-wide QueryClientProvider.
 * - Parse-time prefetching (before React mounts), module-level column
 *   definitions, and other imperative (non-hook) query operations — via the
 *   browser-only `queryClient` export below.
 */

import { isServer, QueryClient } from '@tanstack/react-query';

/**
 * Configuration (preserved from the original singleton):
 * - staleTime: Infinity - disable automatic background refetching by default
 *   (individual queries, e.g. entitlements, override this per-query).
 * - retry: 3 - retry failed queries 3 times
 * - retryDelay: 1000 - 1 second between retries
 */
// Exported so the datasource hook can reference it without re-declaring.
export const GRID_PAGE0_QUERY_KEY = 'contactsGridPage0';

/**
 * Exported so a test harness builds its client exactly as the app does (the
 * defaults below plus the per-key defaults), rather than one that hides them
 * (e.g. a test client with `retry: false` passes where the app retries 3 times).
 */
export function makeQueryClient() {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        retry: 3,
        retryDelay: 1000,
      },
    },
  });
  // SSRM page-0 cache: survives tab switches (5min stale, 10min GC).
  // Set once at construction so it's guaranteed to precede any cache writes.
  client.setQueryDefaults([GRID_PAGE0_QUERY_KEY], {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return client;
}

let browserQueryClient: QueryClient | undefined;

/**
 * Server: always a fresh client — no cross-request bleed, no unbounded
 * server-lifetime cache.
 * Browser: one client per tab, created lazily on first access.
 */
export function getQueryClient(): QueryClient {
  if (isServer) {
    return makeQueryClient();
  }
  return (browserQueryClient ??= makeQueryClient());
}

/**
 * Browser-only imperative singleton, for non-hook callers (module-level AG
 * Grid column defs, etc.) that need to reach the same cache the app's
 * QueryClientProvider uses without a hook.
 *
 * Guarded accessor rather than a bare value: a bare
 * `export const queryClient = getQueryClient()` would eagerly capture
 * whatever `getQueryClient()` returns at MODULE-LOAD time — on the server
 * that's a fresh, throwaway client, silently reintroducing the B1
 * cross-request-singleton footgun the first time this module is imported
 * from a server-reachable path. Throwing on server access makes that
 * mistake fail loudly instead.
 */
export const queryClient = new Proxy({} as QueryClient, {
  get(_target, prop) {
    if (isServer) {
      throw new Error(
        'queryClient is browser-only; use getQueryClient() on the server'
      );
    }
    const client = getQueryClient();
    const value = Reflect.get(client, prop, client);
    // QueryClient methods read private class fields, so they must be called
    // bound to the real instance — invoking them as `queryClient.method()`
    // would otherwise call them with `this` set to this Proxy.
    return typeof value === 'function' ? value.bind(client) : value;
  },
});
